import React, { useEffect, useMemo, useState } from 'react';
import qrcode from 'qrcode-generator';
import { Check, Copy, Download, ShieldCheck, Smartphone } from 'lucide-react';
import { startTwoFactorSetup, confirmTwoFactorSetup } from '../../services/authClient.ts';
import { twoFactorWording } from './twoFactorWording.ts';

/**
 * Setting up an authenticator app, in three steps that each do one thing.
 *
 * The design problem here is not security, it is that most people setting this up have never done it
 * before and are being asked to install something. So: no jargon on the screen (the word "TOTP"
 * appears nowhere), one instruction at a time, and named apps rather than "an authenticator app of
 * your choice", because a person who does not know what one is cannot choose one.
 *
 * The recovery codes get a step of their own, and it does not let you past without ticking a box.
 * This is deliberate friction in the one place it is worth having: these codes are shown exactly
 * once, and the people most likely to click through are the ones most likely to lose their phone.
 */

type Step = 'scan' | 'confirm' | 'codes';

interface TwoFactorSetupProps {
    /** Shown when an adviser must do this before they can work — changes the tone from offer to task. */
    mandatory?: boolean;
    onDone: () => void;
    onCancel?: () => void;
}

/** The QR code as an inline SVG string. No network request, no image host, nothing to block. */
function qrSvg(text: string): string {
    // Type 0 = pick the smallest size that fits. 'M' correction is what every authenticator expects.
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    return qr.createSvgTag({ cellSize: 5, margin: 2, scalable: true });
}

/** Group the secret into fours — nobody can read 32 unbroken characters off a screen. */
function readableSecret(secret: string): string {
    return (secret.match(/.{1,4}/g) ?? [secret]).join(' ');
}

const TwoFactorSetup = ({ mandatory = false, onDone, onCancel }: TwoFactorSetupProps) => {
    // `mandatory` is only ever true for advisers, firm staff and platform admins — the people who
    // know the term. See twoFactorWording.ts for why the name differs by reader.
    const words = twoFactorWording(mandatory);
    const [step, setStep] = useState<Step>('scan');
    const [secret, setSecret] = useState('');
    const [uri, setUri] = useState('');
    const [code, setCode] = useState('');
    const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
    const [savedThem, setSavedThem] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState<'secret' | 'codes' | null>(null);
    const [showSecret, setShowSecret] = useState(false);
    const [trustThisDevice, setTrustThisDevice] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const res = await startTwoFactorSetup();
            if (cancelled) return;
            if (!res.ok) return setError(res.message);
            setSecret(res.secret);
            setUri(res.uri);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const svg = useMemo(() => (uri ? qrSvg(uri) : ''), [uri]);

    const copy = async (text: string, which: 'secret' | 'codes') => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(which);
            setTimeout(() => setCopied(null), 2000);
        } catch {
            /* Clipboard blocked — the text is on screen to be typed or selected by hand. */
        }
    };

    const downloadCodes = () => {
        const body = [
            'AgedCareWise recovery codes',
            '',
            'Each code works once. Keep them somewhere safe and away from your phone —',
            'they are what gets you back in if you lose it.',
            '',
            ...recoveryCodes,
        ].join('\n');
        const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'agedcarewise-recovery-codes.txt';
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleConfirm = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await confirmTwoFactorSetup(code.trim(), trustThisDevice);
            if (!res.ok) {
                setCode('');
                return setError(res.message);
            }
            setRecoveryCodes(res.recoveryCodes);
            setStep('codes');
        } finally {
            setBusy(false);
        }
    };

    const primaryBtn =
        'inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300';
    const quietBtn = 'w-full text-sm text-gray-500 transition hover:text-gray-700';

    if (error && !secret) {
        return (
            <div className="space-y-4 text-center">
                <p className="text-sm text-red-600">{error}</p>
                {onCancel && (
                    <button type="button" onClick={onCancel} className={quietBtn}>
                        Close
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* Where am I, and how much is left? Three dots beats a progress bar for three steps. */}
            <div className="flex items-center justify-center gap-1.5" aria-hidden="true">
                {(['scan', 'confirm', 'codes'] as Step[]).map((s) => (
                    <span
                        key={s}
                        className={`h-1.5 rounded-full transition-all ${
                            s === step ? 'w-6 bg-blue-600' : 'w-1.5 bg-gray-300'
                        }`}
                    />
                ))}
            </div>

            {step === 'scan' && (
                <div className="space-y-4">
                    <div className="flex items-start gap-3 rounded-xl bg-blue-50 p-3.5">
                        <Smartphone size={18} className="mt-0.5 shrink-0 text-blue-600" />
                        <p className="text-sm text-blue-900">
                            {mandatory
                                ? 'Adviser accounts are protected by a code from your phone as well as a password. This takes about a minute.'
                                : 'Add a second step to your login, so a password alone is never enough to get in.'}
                        </p>
                    </div>

                    <div>
                        <p className="text-sm font-semibold text-gray-800">1. Get an authenticator app</p>
                        <p className="mt-1 text-sm text-gray-600">
                            If you don&apos;t already have one, install <strong>Google Authenticator</strong> or{' '}
                            <strong>Microsoft Authenticator</strong> from your phone&apos;s app store. They&apos;re free.
                        </p>
                    </div>

                    <div>
                        <p className="text-sm font-semibold text-gray-800">2. Scan this square</p>
                        <p className="mt-1 text-sm text-gray-600">
                            Open the app, choose <em>add account</em>, and point your camera here.
                        </p>
                        {svg ? (
                            <div
                                className="mx-auto mt-3 w-44 rounded-xl border border-gray-200 bg-white p-3 [&>svg]:h-full [&>svg]:w-full"
                                /*
                                 * The SVG is built in this file from a string we generated ourselves — it is
                                 * never user input and never comes from the network.
                                 */
                                dangerouslySetInnerHTML={{ __html: svg }}
                            />
                        ) : (
                            <div className="mx-auto mt-3 h-44 w-44 animate-pulse rounded-xl bg-gray-100" />
                        )}
                    </div>

                    {/*
                     * The typed alternative matters more than it looks: some readers cannot scan, some
                     * people set this up on the same phone the code is on (so there is no second camera),
                     * and someone helping a parent over the phone has to be able to read it out.
                     */}
                    <div className="rounded-xl border border-gray-200 p-3">
                        {showSecret ? (
                            <>
                                <p className="text-xs text-gray-500">
                                    Or choose &ldquo;enter a setup key&rdquo; in the app and type this:
                                </p>
                                <p className="mt-1.5 select-all break-all font-mono text-sm tracking-wide text-gray-800">
                                    {readableSecret(secret)}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => copy(secret, 'secret')}
                                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700"
                                >
                                    {copied === 'secret' ? <Check size={13} /> : <Copy size={13} />}
                                    {copied === 'secret' ? 'Copied' : 'Copy the key'}
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setShowSecret(true)}
                                className="text-sm text-blue-600 hover:text-blue-700"
                            >
                                Can&apos;t scan it? Type a key instead
                            </button>
                        )}
                    </div>

                    <button type="button" onClick={() => setStep('confirm')} disabled={!secret} className={primaryBtn}>
                        I&apos;ve added it
                    </button>
                    {onCancel && !mandatory && (
                        <button type="button" onClick={onCancel} className={quietBtn}>
                            Not now
                        </button>
                    )}
                </div>
            )}

            {step === 'confirm' && (
                <form
                    className="space-y-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleConfirm();
                    }}
                >
                    <div>
                        <p className="text-sm font-semibold text-gray-800">3. Enter the code it shows</p>
                        <p className="mt-1 text-sm text-gray-600">
                            Your app is now showing a 6-digit code for AgedCareWise. It changes every 30 seconds —
                            type whichever one is showing now.
                        </p>
                    </div>

                    <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={code}
                        autoFocus
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                        className="w-full rounded-xl border border-gray-300 px-4 py-3 text-center font-mono text-2xl tracking-[0.35em] text-gray-900 focus:border-blue-500 focus:outline-none"
                        placeholder="000000"
                        aria-label="6-digit code from your authenticator app"
                    />

                    {/*
                      ASKED, NOT ASSUMED. The first cut trusted this browser automatically — the person
                      had just proved a code on it, so it seemed harmless. It is not harmless on a
                      shared or library computer, and the silent version gives them no way to know it
                      happened, let alone decline. The sign-in screen asks; so does this.
                    */}
                    <label className="flex items-start gap-2.5 text-sm text-gray-600">
                        <input
                            type="checkbox"
                            checked={trustThisDevice}
                            onChange={(e) => setTrustThisDevice(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600"
                        />
                        <span>
                            Don&apos;t ask for a code on this device for 30 days
                            <span className="mt-0.5 block text-xs text-gray-400">
                                Only tick this on a computer that&apos;s yours.
                            </span>
                        </span>
                    </label>

                    {error && <p className="text-sm text-red-600">{error}</p>}

                    <button type="submit" disabled={busy || code.length < 6} className={primaryBtn}>
                        {busy ? 'Checking…' : words.turnOn}
                    </button>
                    <button type="button" onClick={() => { setError(null); setStep('scan'); }} className={quietBtn}>
                        Back
                    </button>
                </form>
            )}

            {step === 'codes' && (
                <div className="space-y-4">
                    <div className="flex items-start gap-3 rounded-xl bg-green-50 p-3.5">
                        <ShieldCheck size={18} className="mt-0.5 shrink-0 text-green-600" />
                        <p className="text-sm text-green-900">
                            <strong>That&apos;s on now.</strong> From here on you&apos;ll enter a code when you sign in
                            on a new device.
                        </p>
                    </div>

                    <div>
                        <p className="text-sm font-semibold text-gray-800">Save these recovery codes</p>
                        <p className="mt-1 text-sm text-gray-600">
                            These get you in if you ever lose your phone. Each one works once.{' '}
                            <strong>We can&apos;t show them to you again</strong> — print them, or keep them somewhere
                            safe that isn&apos;t your phone.
                        </p>
                    </div>

                    <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-xl border border-gray-200 bg-gray-50 p-4 font-mono text-sm text-gray-800">
                        {recoveryCodes.map((c) => (
                            <li key={c} className="select-all">
                                {c}
                            </li>
                        ))}
                    </ul>

                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => copy(recoveryCodes.join('\n'), 'codes')}
                            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-300 px-3 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                        >
                            {copied === 'codes' ? <Check size={15} /> : <Copy size={15} />}
                            {copied === 'codes' ? 'Copied' : 'Copy'}
                        </button>
                        <button
                            type="button"
                            onClick={downloadCodes}
                            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-300 px-3 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                        >
                            <Download size={15} /> Download
                        </button>
                    </div>

                    {/* The one piece of deliberate friction in the flow, and the one worth having. */}
                    <label className="flex items-start gap-2.5 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={savedThem}
                            onChange={(e) => setSavedThem(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600"
                        />
                        <span>I&apos;ve saved these somewhere safe</span>
                    </label>

                    <button type="button" onClick={onDone} disabled={!savedThem} className={primaryBtn}>
                        Done
                    </button>
                </div>
            )}
        </div>
    );
};

export default TwoFactorSetup;
